import React, { useState } from "react";
import theme from "../../../styles/theme";
 

/**
 * DropdownMulti
 * ---------------------------------------------------------------------------
 * Liste déroulante permettant la sélection multiple.
 * Apparence standard cohérente avec les CardComponents (Input, DropdownList…).
 *
 * PROPS :
 *  - label                : texte du label
 *  - options              : [{ value, label }]
 *  - selectedValues       : tableau des valeurs sélectionnées
 *  - onChange             : function(newValues)
 *  - placeholder          : placeholder si aucune sélection
 *  - disabled             : désactivation complète
 *
 *  - width                : largeur du composant
 *  - height               : hauteur du select (interne)
 *  - labelWidth           : largeur du label
 *  - gap                  : espace entre label et champ
 *  - marginTop / marginBottom
 *  - padding              : padding interne du select (ex : "4px 8px")
 *
 *  → Aucun système de palette (standard sobre).
 *  → Style homogène avec le reste de l'application.
 * ---------------------------------------------------------------------------
 */

const DropdownPrint = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Sélectionnez...",
  disabled = false,

  // Layout
  width = "100%",
  height = 32,
  labelWidth = 80,
  gap = 8,
  marginTop = 0,
  marginBottom = theme.spacing.md,
  padding = "4px 8px",
  

  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const isDisabled = disabled;

  // ---------------------------------------------------------------------------
  // Gestion sélection / désélection
  // ---------------------------------------------------------------------------
  const toggleOption = (value) => {
    if (isDisabled) return;

    const exists = selectedValues.includes(value);
    const newValues = exists
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onChange(newValues);
  };

  const toggleSelectAll = () => {
    if (isDisabled) return;

    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  // ---------------------------------------------------------------------------
  // Label affiché dans le select
  // ---------------------------------------------------------------------------
  const displayText =
    selectedValues.length > 0
      ? selectedValues
          .map((v) => options.find((o) => o.value === v)?.label ?? v)
          .join(", ")
      : placeholder;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width,
        marginTop,
        marginBottom,
        gap,
        position: "relative",
        ...style,
      }}
    >
      {/* LABEL */}
      {label && (
        <label
          style={{
            width: labelWidth,
            minWidth: labelWidth,
            fontFamily: theme.form.label.fontFamily,
            fontSize: theme.form.label.fontSize,
            fontWeight: theme.form.label.fontWeight,
            color: theme.form.label.color,
          }}
        >
          {label}
        </label>
      )}

      {/* SELECT BOX */}
      <div
        onClick={() => !isDisabled && setOpen(!open)}
        style={{
          flex: 1,
          height,
          padding,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: theme.borders.radius.md,
          border: `1px solid ${theme.colors.borders.default}`,
          backgroundColor: isDisabled
            ? theme.buttons.disabled.background
            : theme.colors.background.input,
          cursor: isDisabled ? "not-allowed" : "pointer",
          fontFamily: theme.form.field.fontFamily,
          fontSize: theme.form.field.fontSize,
          color: selectedValues.length
            ? theme.colors.text.default
            : theme.colors.text.placeholder,
          fontStyle: selectedValues.length ? "normal" : "italic",
        }}
      >
        <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
          {displayText}
        </span>

        <span
          style={{
            fontSize: 12,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          ▼
        </span>
      </div>

      {/* OPTIONS LIST */}
      {open && !isDisabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: labelWidth + gap,
            right: 0,
            maxHeight: 200,
            overflowY: "auto",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.sm,
            backgroundColor: theme.colors.background.surface,
            boxShadow: theme.shadows.md,
            zIndex: 9999,
            marginTop: 2,
          }}
        >
          {/* Select All */}
          <div
            onClick={toggleSelectAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              cursor: "pointer",
              backgroundColor: theme.colors.background.muted,
              borderBottom: `1px solid ${theme.colors.borders.default}`,
              fontWeight: theme.fontWeights.medium,
            }}
          >
            <input
              type="checkbox"
              checked={selectedValues.length === options.length && options.length > 0}
              readOnly
              style={{
                cursor: "pointer",
                accentColor: theme.buttons.green.background,
              }}
            />
            <span>
              {selectedValues.length === options.length
                ? "Tout désélectionner"
                : "Tout sélectionner"}
            </span>
          </div>

          {/* OPTIONS */}
          {options.map((opt) => {
            const checked = selectedValues.includes(opt.value);
            return (
              <div
                key={opt.value}
                onClick={() => toggleOption(opt.value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  cursor: "pointer",
                  backgroundColor: checked
                    ? theme.colors.background.muted
                    : "transparent",
                  transition: "background-color 0.2s ease",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  style={{
                    cursor: "pointer",
                    accentColor: theme.buttons.green.background,
                  }}
                />
                <span>{opt.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropdownPrint;
